import Logger from '@/utils/logger'
import { 
  getBannerList as apiGetBannerList,
  createBanner as apiCreateBanner,
  updateBanner as apiUpdateBanner,
  deleteBanner as apiDeleteBanner,
  updateBannerSort as apiUpdateBannerSort
} from '@/api/banners'

const state = {
  bannerList: [],
  currentBanner: null,
  loading: false,
  pagination: {
    page: 1,
    size: 20,
    total: 0
  },
  pendingStatusChanges: new Set()
}

const mutations = {
  SET_BANNER_LIST(state, { list, pagination }) {
    state.bannerList = list
    if (pagination) {
      state.pagination = { ...state.pagination, ...pagination }
    }
  },
  SET_CURRENT_BANNER(state, banner) {
    state.currentBanner = banner
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  ADD_BANNER(state, banner) {
    state.bannerList.unshift(banner)
  },
  UPDATE_BANNER_IN_LIST(state, updatedBanner) {
    const index = state.bannerList.findIndex(banner => banner.id === updatedBanner.id)
    if (index !== -1) {
      state.bannerList.splice(index, 1, { ...state.bannerList[index], ...updatedBanner })
    }
  },
  REMOVE_BANNER_FROM_LIST(state, bannerId) {
    const index = state.bannerList.findIndex(banner => banner.id === bannerId)
    if (index !== -1) {
      state.bannerList.splice(index, 1)
    }
  },
  UPDATE_BANNER_ORDER(state, banners) {
    state.bannerList = banners
  },
  UPDATE_BANNER_STATUS(state, { id, isActive }) {
    const index = state.bannerList.findIndex(banner => banner.id === id)
    if (index !== -1) {
      // Create a new banner object with updated status to maintain immutability
      state.bannerList.splice(index, 1, {
        ...state.bannerList[index],
        isActive
      })
    }
  },
  ADD_PENDING_STATUS_CHANGE(state, bannerId) {
    state.pendingStatusChanges.add(bannerId)
  },
  REMOVE_PENDING_STATUS_CHANGE(state, bannerId) {
    state.pendingStatusChanges.delete(bannerId)
  }
}

const actions = {
  async fetchBanners({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await apiGetBannerList(params)
      
      commit('SET_BANNER_LIST', {
        list: response.data.list,
        pagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total
        }
      })
      
      return response
    } catch (error) {
      console.error('获取轮播图列表失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async createBanner({ commit }, data) {
    try {
      const response = await apiCreateBanner(data)
      
      commit('ADD_BANNER', response.data)
      
      // 记录操作日志
      Logger.recordBannerAction('创建', response.data, data, { success: true })
      
      return response
    } catch (error) {
      console.error('创建轮播图失败:', error)
      throw error
    }
  },
  
  async updateBanner({ commit }, { id, data }) {
    try {
      const response = await apiUpdateBanner(id, data)
      
      commit('UPDATE_BANNER_IN_LIST', response.data)
      commit('SET_CURRENT_BANNER', response.data)
      
      // 记录操作日志
      Logger.recordBannerAction('更新', response.data, data, { success: true })
      
      return response
    } catch (error) {
      console.error('更新轮播图失败:', error)
      throw error
    }
  },
  
  async deleteBanner({ commit, state }, id) {
    try {
      const banner = state.bannerList.find(banner => banner.id === id)
      const response = await apiDeleteBanner(id)
      
      commit('REMOVE_BANNER_FROM_LIST', id)
      
      // 记录操作日志
      if (banner) {
        Logger.recordBannerAction('删除', banner, { id }, { success: true })
      }
      
      return response
    } catch (error) {
      console.error('删除轮播图失败:', error)
      throw error
    }
  },
  
  async updateBannerSort({ commit }, banners) {
    try {
      const response = await apiUpdateBannerSort({ banners })
      
      commit('UPDATE_BANNER_ORDER', banners)
      
      // 记录操作日志
      Logger.recordBannerAction('排序', { count: banners.length }, { banners }, { success: true })
      
      return response
    } catch (error) {
      console.error('更新轮播图排序失败:', error)
      throw error
    }
  },
  
  async updateBannerStatus({ commit, state }, { id, isActive }) {
    // Check if there's already a pending status change for this banner
    if (state.pendingStatusChanges.has(id)) {
      console.warn(`Status change already pending for banner ${id}`)
      return
    }
    
    // Find the current banner to get original status for rollback
    const banner = state.bannerList.find(b => b.id === id)
    if (!banner) {
      throw new Error(`Banner with id ${id} not found`)
    }
    
    const originalStatus = banner.isActive
    
    try {
      // Mark as pending to prevent concurrent updates
      commit('ADD_PENDING_STATUS_CHANGE', id)
      
      // Optimistic update - immediately update UI
      commit('UPDATE_BANNER_STATUS', { id, isActive })
      
      // Make API call
      const response = await apiUpdateBanner(id, { isActive })
      
      // Update with server response to ensure consistency
      commit('UPDATE_BANNER_IN_LIST', response.data)
      
      // 记录操作日志
      Logger.recordBannerAction('状态更新', response.data, { isActive }, { success: true })
      
      return response
    } catch (error) {
      console.error('更新轮播图状态失败:', error)
      
      // Rollback optimistic update on error
      commit('UPDATE_BANNER_STATUS', { id, isActive: originalStatus })
      
      // 记录错误日志
      Logger.recordBannerAction('状态更新', banner, { isActive }, { success: false, error: error.message })
      
      throw error
    } finally {
      // Always remove from pending set
      commit('REMOVE_PENDING_STATUS_CHANGE', id)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}